import React, {
  forwardRef,
  useContext,
  useCallback,
  useEffect,
  useMemo,
  useImperativeHandle,
  useState,
  useRef,
} from "react";

import { PlusOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { Button } from "antd";
import { motion, AnimatePresence } from "framer-motion/dist/framer-motion";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";

import StyledScrollbar from "../../../Common/UIComponents/StyledScrollbar";
import StyledTooltip from "../../../Common/UIComponents/StyledTooltip";
import SampleFunctionContext from "../../SampleFunctionContext";
import SampleSubmissionContext from "../../SampleSubmissionContext";
import {
  isTestNameNotDoableForSide,
  isTestNotDoableForSide,
  notDoableTestMessage,
  sampleHasNotDoableTest,
} from "../../utils";
import { CHAR_LIMITS } from "../Constant";
import AnalysisRequest from "./AnalysisRequest/AnalysisRequest";
import AddUpdateSampleButton from "./Buttons/AddUpdateSampleButton";
import SampleDetails from "./SampleDetails/SampleDetails";
import SampleID from "./SampleID";
// import SampleIssueEditFooter from "./SampleIssueEditFooter";
import SampleListViewModal from "./SampleListViewModal";
// import ExitButton from "./Buttons/ExitButton";

import { getCustomerConfig } from "../../../../utils/common";
import { hasPermission, permissions } from "../../../../utils/Permissions";
import { isPrepaidOrSubscriptionCustomer } from "../../../MyAccount/UsageMetrics/utils";

import { fetchUsageCredits } from "../../../../actions/usage";

import { ReactComponent as CompositeSamplesIcon } from "../../../../assets/icons/Sample Submission Redesign/composite-samples.svg";
import { ReactComponent as DeleteBinLineIcon } from "../../../../assets/icons/Sample Submission Redesign/delete-bin-line.svg";
import { ReactComponent as FileCopy2LineIcon } from "../../../../assets/icons/Sample Submission Redesign/file-copy-2-line.svg";
import { ReactComponent as PencilLineIcon } from "../../../../assets/icons/Sample Submission Redesign/pencil-line.svg";
import "./SampleDetailsPage.css";

const SampleDetailsPage = forwardRef((props, ref) => {
  const {
    inputValuesRefs,
    setInputValuesRefs,
    inputValuesSavedForNext,
    toggleSaveForNext,
    clearSampleDetailsForm,
    setClearSampleDetailsForm,
    loadDraftState,
    setLoadDraftState,
    loadSampleEditing,
    setLoadSampleEditing,
    sampleEditing,
    handleGoBackToAddressPage,
    setInvalidInputFound,
    savedState,
    loadSavedState,
    setLoadSavedState,
    setSavedState,
    setTestsSelected,
    saveTestsForNext,
    testsSelected,
    generateSampleID,
    sampleIDGenerationError,
    setInputValueAutofilled,
    handleGoBackToMainPage,
    generatedSampleID,
    clearTestSearch,
    setClearTestSearch,
    customerDetails,
    waitingForAddEditAPI,
    sampleEditingIdx,
    handleSaveEditedSample,
    handleAddSample,
    handleCancelEditSample,
    invalidInputFound,
    invalidMandatoryFound,
    setInvalidMandatoryFound,
    sampleList,
    handleEditSample,
    handleDeleteClick,
    handleSubmitButtonClick,
    // handleIssueFlowDeleteClick,
    draftEditing,
    sampleFields,
    sampleFieldsInfo,
    delimiterRegex,
    imageInfoRef,
    setImageInfoRef,
    setLoadingImage,
    submissionEditingSample,
    submissionAddingSamples,
    sampleIdFields,
    invalidTest,
    setInvalidTest,
    testInfoMap,
    sampleFromSubmission,
    submissionDetails,
    pesticideSample,
    setPesticideSample,
    sampleCategory,
    setShowExitModal,
    isLoadingSampleId,
    setIsLoadingSampleId,
    compositeSample,
    editingSubmittedSampleComposite,
    setEditingSubmittedSampleComposite,
    clearCompositeSaveForNext,
    onSaveDraftForRequestNew,
  } = props;
  const [sampleIDLoaded, setSampleIDLoaded] = useState(false);
  const [addLoading, setAddLoading] = useState(false);
  const [isLoadingTest, setIsLoadingTest] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [lockedSampleDetailsWidth, setLockedSampleDetailsWidth] = useState(null);
  const [sampleListViewModalOpen, setSampleListViewModalOpen] = useState(false);
  const [creditsResponse, setCreditsResponse] = useState(null);
  const [creditsLoaded, setCreditsLoaded] = useState(false);
  const [expandFormFromSidebar, setExpandFormFromSidebar] = useState(false);
  const prevSampleListLenRef = useRef(sampleList.length);
  const prevSampleEditingIdxRef = useRef(sampleEditingIdx);

  const isIssueOverview = Boolean(
    submissionEditingSample?.issueFlow
    && sampleEditingIdx === -1
    && sampleList.length > 0,
  );
  const showExpandedForm = !isIssueOverview && (
    sampleList.length === 0
    || sampleEditingIdx > -1
    || expandFormFromSidebar
    || loadSavedState
  );
  const { isLoggedOut, thirdParty, mrl } = useContext(SampleSubmissionContext);
  const { backRef } = useContext(SampleFunctionContext);
  const disableAddSample = submissionEditingSample && sampleEditingIdx === -1; // disables inputs, tests, and hides (not just disables) add sample button
  const analysisRequestRef = useRef();
  const sampleDetailsColumnRef = useRef(null);
  const sampleListScrollRef = useRef(null);
  const mainFormScrollRef = useRef(null);
  const sidebarItemRefs = useRef({});
  const isUsageCustomer = isPrepaidOrSubscriptionCustomer(getCustomerConfig()?.customer_type);

  useEffect(() => {
    if (!isUsageCustomer) {
      return undefined;
    }

    const controller = new AbortController();
    let isMounted = true;

    const loadCredits = async () => {
      const response = await fetchUsageCredits({ signal: controller.signal });
      if (!isMounted || response.canceled) {
        return;
      }

      if (!response.success) {
        toast.error(response.message || "Unable to load remaining credits.");
        return;
      }

      setCreditsResponse(response);
      setCreditsLoaded(true);
    };

    loadCredits();

    return () => {
      isMounted = false;
      controller.abort();
    };
  }, [isUsageCustomer]);

  const handleOverviewSubmit = () => {
    if (isUsageCustomer) {
      setSampleListViewModalOpen(true);
      return;
    }
    handleSubmitButtonClick();
  };

  const scrollMainFormToBottom = useCallback(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        if (typeof mainFormScrollRef.current?.scrollToBottom === "function") {
          mainFormScrollRef.current.scrollToBottom();
        }
      });
    });
  }, []);

  const handleToggleMaximize = useCallback(() => {
    if (!isMaximized) {
      const w = sampleDetailsColumnRef.current?.getBoundingClientRect().width;
      if (w) {
        setLockedSampleDetailsWidth(Math.round(w));
      }
    } else {
      setLockedSampleDetailsWidth(null);
    }
    setIsMaximized((m) => !m);
  }, [isMaximized]);
  /** Close ref for the sample submission details */
  useImperativeHandle(ref, () => ({
    closeFunction() {
      if (thirdParty) {
        handleGoBackToAddressPage();
      } else if (!isLoggedOut) {
        handleGoBackToMainPage();
      }
    },
    breadcrumbBackFunction() {
      handleGoBackToAddressPage();
    },
  }));

  /**
   * Check if pesticide sample type field is empty for pesticide
   */
  const isPesticideSampleEmpty = useMemo(() => {
    if (isEmpty(testsSelected)) {
      return false;
    }
    const addedMRLTest = Object.values(testsSelected).find((test) => test.test_category === "MRL");
    return addedMRLTest && !pesticideSample.trim() && sampleCategory !== "environment" && (hasPermission(permissions.global.specialPesticides) || mrl);
  }, [testsSelected, pesticideSample, sampleCategory]);  // eslint-disable-line 

  useEffect(() => {
    if (sampleEditing && Object.keys(sampleEditing).length > 0) {
      if (sampleCategory !== "environment" && (hasPermission(permissions.global.specialPesticides) || mrl)) {
        setPesticideSample(sampleEditing.sample_type);
      }
    }
  }, [sampleEditing, setPesticideSample, sampleCategory]); // eslint-disable-line 

  useEffect(() => {
    if (isLoadingSampleId || isLoadingTest) {
      setAddLoading(true);
    } else {
      setAddLoading(false);
    }
  }, [isLoadingSampleId, isLoadingTest]);

  useEffect(() => {
    if (!showExpandedForm) {
      setSampleIDLoaded(false);
    }
  }, [showExpandedForm]);

  useEffect(() => {
    if (showExpandedForm) return;
    if (loadDraftState) setLoadDraftState(false);
    if (loadSampleEditing) setLoadSampleEditing(false);
    if (loadSavedState) setLoadSavedState(false);
    if (clearSampleDetailsForm) setClearSampleDetailsForm(false);
  }, [
    showExpandedForm,
    loadDraftState,
    loadSampleEditing,
    loadSavedState,
    clearSampleDetailsForm,
    setLoadDraftState,
    setLoadSampleEditing,
    setLoadSavedState,
    setClearSampleDetailsForm,
  ]);

  useEffect(() => {
    if (sampleList.length > prevSampleListLenRef.current && sampleEditingIdx === -1) {
      setExpandFormFromSidebar(false);
    }
    prevSampleListLenRef.current = sampleList.length;
  }, [sampleList.length, sampleEditingIdx]);

  useEffect(() => {
    if (prevSampleEditingIdxRef.current > -1 && sampleEditingIdx === -1 && sampleList.length > 0) {
      setExpandFormFromSidebar(false);
    }
    prevSampleEditingIdxRef.current = sampleEditingIdx;
  }, [sampleEditingIdx, sampleList.length]);

  useEffect(() => {
    if (sampleEditingIdx < 0) return;
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const activeSidebarItem = sidebarItemRefs.current[sampleEditingIdx];
        if (activeSidebarItem && typeof activeSidebarItem.scrollIntoView === "function") {
          activeSidebarItem.scrollIntoView({ block: "nearest", behavior: "smooth" });
        }
      });
    });
  }, [sampleEditingIdx]);

  const sampleHasNotDoableTests = (sample) => sampleHasNotDoableTest(sample, testInfoMap, sampleCategory);

  const sampleHasBlockingIssue = (sample) => {
    const sampleIDCharLimitExceeded = sample.sample_id.length > CHAR_LIMITS.sample_id;
    const sampleTypeCharLimitExceeded = (sample.sample_type?.length ?? 0) > CHAR_LIMITS.sample_type;
    const sampleTestsMissing = (sample.test_list?.length ?? 0) === 0;
    return sampleIDCharLimitExceeded || sampleTypeCharLimitExceeded || sampleTestsMissing || sampleHasNotDoableTests(sample);
  };

  const checkForSampleErrors = () => {
    if (sampleList.length === 0) return true;
    if (sampleEditingIdx > -1) return true;

    return sampleList.some(sampleHasBlockingIssue);
  };

  const getSubmissionErrorText = () => {
    if (sampleList.length === 0) {
      return "No samples added yet.";
    }
    if (sampleEditingIdx > -1) {
      return "Cannot submit while editing";
    }
    if (sampleList.some((sample) => (sample.test_list?.length ?? 0) === 0)) {
      return "Each sample must have at least one test.";
    }
    if (sampleList.some(sampleHasNotDoableTests)) {
      return notDoableTestMessage(sampleCategory);
    }
    if (checkForSampleErrors()) {
      return "Please correct the invalid sample inputs before proceeding.";
    }
    if (isUsageCustomer && !creditsLoaded) {
      return "Unable to load remaining credits.";
    }
    return "";
  };

  const isSubmitDisabled = sampleList.length === 0
    || sampleEditingIdx > -1
    || checkForSampleErrors()
    || (isUsageCustomer && !creditsLoaded);

  /**
   *Customized button for add or updatung the sample submission details
   */
  const addSampleButton = () => (
    <AddUpdateSampleButton
      waitingForAddEditAPI={waitingForAddEditAPI}
      sampleEditingIdx={sampleEditingIdx}
      handleSaveEditedSample={handleSaveEditedSample}
      handleAddSample={handleAddSample}
      handleCancelEditSample={handleCancelEditSample}
      invalidInputFound={invalidInputFound}
      invalidMandatoryFound={invalidMandatoryFound}
      invalidTest={invalidTest}
      notDoableTest={Object.entries(testsSelected || {}).some(([name, testObj]) => (
        isTestNotDoableForSide(testObj, sampleCategory)
        || isTestNotDoableForSide(testInfoMap.get(name), sampleCategory)
      ))}
      sampleCategory={sampleCategory}
      sampleIDGenerationError={sampleIDGenerationError}
      disableAddSample={disableAddSample}
      generatedSampleID={generatedSampleID}
      submissionEditingSample={submissionEditingSample}
      isPesticideSampleEmpty={isPesticideSampleEmpty}
      isLoading={addLoading}
      setEditingSubmittedSampleComposite={setEditingSubmittedSampleComposite}
      noTestsSelected={isEmpty(testsSelected)}
    />
  );

  const getSampleSidebarLines = (sample) => sampleFields
    .filter((field) => field !== "sample_id" && sample[field])
    .map((field) => `${sampleFieldsInfo[field]?.title_field ?? field}: ${sample[field]}`);

  const handleDuplicatePrevious = async () => {
    if (waitingForAddEditAPI) return;

    if (sampleEditingIdx > -1) {
      await handleSaveEditedSample(false);
    }

    const previousSample = sampleList[sampleList.length - 1];
    if (!previousSample) {
      return;
    }
    const duplicateFields = {};
    sampleFields.forEach((field) => {
      duplicateFields[field] = {
        value: previousSample[field] ?? "",
        autofilled: false,
      };
    });
    const duplicateTests = {};
    (previousSample.test_list ?? []).forEach((testName) => {
      duplicateTests[testName] = { ...(testInfoMap.get(testName) ?? {}) };
    });
    const duplicateHasMrlTest = Object.keys(duplicateTests).some(
      (testName) => duplicateTests[testName]?.test_category === "MRL",
    );
    const compositeSampleCount = previousSample.num_composite_samples ?? 0;
    setSavedState({
      fields: duplicateFields,
      savedForNext: { ...inputValuesSavedForNext },
      tests: duplicateTests,
      saveTests: false,
      composite: compositeSampleCount > 0
        ? {
          sample_count: compositeSampleCount,
          identifier: previousSample.composite_fieldnames ?? [],
        }
        : null,
    });
    setPesticideSample(duplicateHasMrlTest ? (previousSample.sample_type ?? "") : "");
    setLoadSavedState(true);
    setExpandFormFromSidebar(true);
    scrollMainFormToBottom();
  };

  const handleStartNewSample = async () => {
    if (waitingForAddEditAPI) return;
    if (sampleEditingIdx > -1) {
      await handleSaveEditedSample(false);
    }
    setExpandFormFromSidebar(true);
    setSavedState(null);
    setLoadSavedState(false);
    setClearSampleDetailsForm(true);
    setPesticideSample("");
    setClearTestSearch(true);
    scrollMainFormToBottom();
  };

  const handleDiscardPendingNewSample = () => {
    if (sampleEditingIdx > -1) return;
    setExpandFormFromSidebar(false);
    setSavedState(null);
    setLoadSavedState(false);
    setClearSampleDetailsForm(false);
    setPesticideSample("");
    setClearTestSearch(true);
  };

  const minimizedSamplesBefore = sampleEditingIdx >= 0 ? sampleList.slice(0, sampleEditingIdx) : sampleList;
  const minimizedSamplesAfter = sampleEditingIdx >= 0 ? sampleList.slice(sampleEditingIdx + 1) : [];
  const noSamplesAddedYet = sampleList.length === 0;

  const renderMinimizedSampleCards = (samples) => samples.map((sample) => {
    const actualIdx = sampleList.indexOf(sample);
    return (
      <div key={sample.uuid} className={`SampleDetailsPage__MinimizedCard${sampleHasNotDoableTests(sample) ? " SampleDetailsPage__MinimizedCard--notDoable" : ""}`}>
        <div className="SampleDetailsPage__MinimizedCardTop">
          <div className={`SampleDetailsPage__MinimizedCardTitle${sampleHasNotDoableTests(sample) ? " SampleDetailsPage__MinimizedCardTitle--notDoable" : ""}`}>{`${actualIdx + 1}. ${sample.sample_id || "Untitled Sample"}`}</div>
          <div className="SampleDetailsPage__MinimizedCardActions">
            <span
              className="SampleDetailsPage__iconHitTarget"
              role="button"
              tabIndex={0}
              aria-label="Edit sample"
              onClick={() => handleEditSample(actualIdx)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleEditSample(actualIdx);
                }
              }}
            >
              <PencilLineIcon aria-hidden className="SampleDetailsPage__svgIcon SampleDetailsPage__svgIcon--pencil" />
            </span>
            {!submissionEditingSample?.issueFlow && (
            <span
              className="SampleDetailsPage__iconHitTarget"
              role="button"
              tabIndex={0}
              aria-label="Delete sample"
              onClick={() => handleDeleteClick(actualIdx)}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  handleDeleteClick(actualIdx);
                }
              }}
            >
              <DeleteBinLineIcon aria-hidden className="SampleDetailsPage__svgIcon SampleDetailsPage__svgIcon--deleteBin" />
            </span>
            )}
          </div>
        </div>
        <div className="SampleDetailsPage__MinimizedCardBottom">
          <div className="SampleDetailsPage__MinimizedCardBottomLeft">
            <span className="SampleDetailsPage__MinimizedTestsLabel">
              {`Tests Selected (${sample.test_list?.length ?? 0})`}
            </span>
          </div>
          <div className="SampleDetailsPage__MinimizedCardBottomRight">
            {(sample.test_list ?? []).map((testName) => (
              <span
                key={`${sample.uuid}-${testName}`}
                className={`SampleDetailsPage__MinimizedTestPill${isTestNameNotDoableForSide(testName, testInfoMap, sampleCategory) ? " SampleDetailsPage__MinimizedTestPill--notDoable" : ""}`}
              >
                {testName}
              </span>
            ))}
          </div>
        </div>
      </div>
    );
  });

  const sampleListSidebar = !submissionEditingSample && !isMaximized && (
    <motion.div className="new-submission-pages-col SampleDetailsPage__SampleListColumn">
      <div className="new-submission-pages-card SampleDetailsPage__SampleListCard">
        <div className="SampleDetailsPage__OverviewSidebar">
          <div className="SampleDetailsPage__OverviewSidebarTitle">Sample List</div>
          <StyledScrollbar
            ref={sampleListScrollRef}
            className="SampleDetailsPage__SampleListScroll"
          >
            <div className="SampleDetailsPage__OverviewSidebarScrollBody">
              <div className="SampleDetailsPage__OverviewSidebarList">
                {sampleList.length === 0 && (
                  <div className="SampleDetailsPage__OverviewSidebarItem SampleDetailsPage__OverviewSidebarItem--placeholder">
                    <div className="SampleDetailsPage__OverviewSidebarItemTitle">1.</div>
                  </div>
                )}
                {sampleList.map((sample, idx) => {
                  const isComposite = (sample.num_composite_samples ?? 0) > 0;
                  const isEditingThisSample = sampleEditingIdx >= 0 && idx === sampleEditingIdx;
                  return (
                    <div
                      key={sample.uuid}
                      ref={(el) => {
                        if (el) {
                          sidebarItemRefs.current[idx] = el;
                        } else {
                          delete sidebarItemRefs.current[idx];
                        }
                      }}
                      className={`SampleDetailsPage__OverviewSidebarItem ${isComposite ? "SampleDetailsPage__OverviewSidebarItem--composite" : ""}${sampleHasBlockingIssue(sample) ? " highlighted-sample-card--error" : ""}${sampleHasNotDoableTests(sample) ? " SampleDetailsPage__OverviewSidebarItem--notDoable" : ""}${isEditingThisSample ? " SampleDetailsPage__OverviewSidebarItem--editing" : ""}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        handleEditSample(idx);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          handleEditSample(idx);
                        }
                      }}
                    >
                      <div className="SampleDetailsPage__OverviewSidebarItemHeader">
                        <div className="SampleDetailsPage__OverviewSidebarItemTitle">
                          {`${idx + 1}. ${sample.sample_id || "Untitled Sample"}`}
                        </div>
                        {isEditingThisSample && (
                          <span
                            className="SampleDetailsPage__iconHitTarget SampleDetailsPage__OverviewSidebarItemDelete"
                            role="button"
                            tabIndex={0}
                            aria-label="Delete sample"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleDeleteClick(idx);
                            }}
                            onKeyDown={(e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                e.stopPropagation();
                                handleDeleteClick(idx);
                              }
                            }}
                          >
                            <DeleteBinLineIcon aria-hidden className="SampleDetailsPage__svgIcon SampleDetailsPage__svgIcon--deleteBin" />
                          </span>
                        )}
                      </div>
                      {getSampleSidebarLines(sample).map((line) => (
                        <div key={`${sample.uuid}-${line}`} className="SampleDetailsPage__OverviewSidebarItemLine">
                          {line}
                        </div>
                      ))}
                      {isComposite && (
                        <div className="SampleDetailsPage__OverviewSidebarCompositeFooter">
                          <span className="SampleDetailsPage__OverviewSidebarCompositeBadge">
                            <CompositeSamplesIcon
                              aria-hidden
                              className="SampleDetailsPage__OverviewSidebarCompositeBadgeIcon"
                            />
                            <span>Composite Sample</span>
                          </span>
                          <span className="SampleDetailsPage__OverviewSidebarCompositeCount">
                            {`${sample.num_composite_samples} Samples`}
                          </span>
                        </div>
                      )}
                    </div>
                  );
                })}
                {sampleList.length > 0 && sampleEditingIdx === -1 && expandFormFromSidebar && (
                  <div className="SampleDetailsPage__OverviewSidebarItem SampleDetailsPage__OverviewSidebarItem--placeholder SampleDetailsPage__OverviewSidebarItem--editing">
                    <div className="SampleDetailsPage__OverviewSidebarItemHeader SampleDetailsPage__OverviewSidebarItemHeader--pending">
                      <div className="SampleDetailsPage__OverviewSidebarItemTitle">{`${sampleList.length + 1}.`}</div>
                      {sampleList.length > 0 && (
                        <span
                          className="SampleDetailsPage__iconHitTarget SampleDetailsPage__OverviewSidebarItemDelete"
                          role="button"
                          tabIndex={0}
                          aria-label="Delete pending sample"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDiscardPendingNewSample();
                          }}
                          onKeyDown={(e) => {
                            if (e.key === "Enter" || e.key === " ") {
                              e.preventDefault();
                              e.stopPropagation();
                              handleDiscardPendingNewSample();
                            }
                          }}
                        >
                          <DeleteBinLineIcon aria-hidden className="SampleDetailsPage__svgIcon SampleDetailsPage__svgIcon--deleteBin" />
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>
              <div className="SampleDetailsPage__segmentedActions" role="group" aria-label="Add sample">
                <button
                  type="button"
                  className="SampleDetailsPage__segmentedBtn SampleDetailsPage__segmentedBtn--left"
                  disabled={noSamplesAddedYet || waitingForAddEditAPI}
                  onClick={handleStartNewSample}
                >
                  <PlusOutlined className="SampleDetailsPage__segmentedBtnIcon" />
                  <span>New</span>
                </button>
                <button
                  type="button"
                  className="SampleDetailsPage__segmentedBtn SampleDetailsPage__segmentedBtn--right"
                  disabled={noSamplesAddedYet}
                  onClick={handleDuplicatePrevious}
                >
                  <FileCopy2LineIcon aria-hidden className="SampleDetailsPage__svgIcon SampleDetailsPage__svgIcon--fileCopy SampleDetailsPage__svgIcon--fileCopySegmented" />
                  <span>Duplicate previous</span>
                </button>
              </div>
            </div>
          </StyledScrollbar>
          <div className="SampleDetailsPage__OverviewSidebarFooter">
            <Button
              type="default"
              htmlType="button"
              className="SampleDetailsPage__OverviewGhostBtn"
              onClick={() => setSampleListViewModalOpen(true)}
              disabled={noSamplesAddedYet}
              icon={<UnorderedListOutlined className="SampleDetailsPage__OverviewGhostBtnIcon" aria-hidden />}
            >
              <span>View as list</span>
            </Button>
            <div className="SampleDetailsPage__OverviewFooterSubmitCell">
              <StyledTooltip
                title={getSubmissionErrorText()}
                placement="top"
              >
                <span className="SampleDetailsPage__OverviewFooterSubmitTooltipWrap">
                  <Button
                    type="primary"
                    htmlType="button"
                    className="SampleDetailsPage__OverviewFooterSubmit"
                    disabled={isSubmitDisabled}
                    onClick={handleOverviewSubmit}
                  >
                    <span>Submit</span>
                  </Button>
                </span>
              </StyledTooltip>
            </div>
          </div>
        </div>
      </div>
    </motion.div>
  );

  if (isIssueOverview) {
    return (
      <div className="SampleDetailsPage__IssueOverview">
        <div className="SampleDetailsPage__IssueOverviewBody">
          <div className="SampleDetailsPage__MinimizedList">
            {renderMinimizedSampleCards(sampleList)}
          </div>
        </div>
        {/* <SampleIssueEditFooter
          sample={sampleList[0]}
          poNumber={submissionDetails.poNumber}
          onDelete={handleIssueFlowDeleteClick}
          onSubmit={handleSubmitButtonClick}
          submitDisabled={isSubmitDisabled}
          submitTooltip={getSubmissionErrorText()}
        /> */}
      </div>
    );
  }

  return (
    <>
      <SampleListViewModal
        open={sampleListViewModalOpen}
        onCancel={() => setSampleListViewModalOpen(false)}
        sampleList={sampleList}
        sampleFields={sampleFields}
        sampleFieldsInfo={sampleFieldsInfo}
        sampleCategory={sampleCategory}
        submissionDetails={submissionDetails}
        onSelectTestQuantity={onSaveDraftForRequestNew}
        onSubmitSamples={handleSubmitButtonClick}
        creditsResponse={creditsResponse}
        submitDisabled={isSubmitDisabled}
        submitTooltip={getSubmissionErrorText()}
        testInfoMap={testInfoMap}
        creditsLoaded={creditsLoaded}
      />
      <div className="SampleDetailsPage__FormWrapper">
        <div
          className={`sample-details-analysis-merged-column ${submissionEditingSample ? "sample-details-analysis-merged-column-edit" : ""}${isMaximized ? " sample-details-analysis-merged-column--maximized" : ""}`}
          style={lockedSampleDetailsWidth != null
            ? { gridTemplateColumns: `${lockedSampleDetailsWidth}px minmax(0, 1fr)` }
            : undefined}
        >
          <div className="SampleDetailsPage__MainFormScroll">
            <StyledScrollbar ref={mainFormScrollRef} className="SampleDetailsPage__MainFormScrollbar">
              <div className="SampleDetailsPage__MainFormColumn">
                {minimizedSamplesBefore.length > 0 && (
                  <div className="SampleDetailsPage__MinimizedList">
                    {renderMinimizedSampleCards(minimizedSamplesBefore)}
                  </div>
                )}
                {showExpandedForm && (
                  <div
                    className="SampleDetailsPage__ExpandedFormGrid"
                    style={lockedSampleDetailsWidth != null
                      ? { gridTemplateColumns: `${lockedSampleDetailsWidth}px minmax(0, 1fr)` }
                      : undefined}
                  >
                    {(!isLoggedOut || thirdParty) && (
                      <SampleID
                        sampleID={generatedSampleID}
                        disableAddSample={disableAddSample}
                        sampleIDGenerationError={sampleIDGenerationError}
                        submissionEditingSample={submissionEditingSample}
                        sampleNumber={sampleEditingIdx >= 0 ? sampleEditingIdx + 1 : (sampleList?.length ?? 0) + 1}
                        isMaximized={isMaximized}
                        onToggleMaximize={handleToggleMaximize}
                        showMaximize={!submissionEditingSample}
                        onLoad={() => {
                          setSampleIDLoaded(true);
                        }}
                      />
                    )}
                    <SampleDetails
                      inputValuesRefs={inputValuesRefs}
                      setInputValuesRefs={setInputValuesRefs}
                      inputValuesSavedForNext={inputValuesSavedForNext}
                      toggleSaveForNext={toggleSaveForNext}
                      sampleFields={sampleFields}
                      sampleFieldsInfo={sampleFieldsInfo}
                      clearSampleDetailsForm={clearSampleDetailsForm}
                      setClearSampleDetailsForm={setClearSampleDetailsForm}
                      draftEditing={draftEditing}
                      loadDraftState={loadDraftState}
                      setLoadDraftState={setLoadDraftState}
                      loadSampleEditing={loadSampleEditing}
                      setLoadSampleEditing={setLoadSampleEditing}
                      sampleEditing={sampleEditing}
                      handleClickBackButton={handleGoBackToAddressPage}
                      delimiterRegex={delimiterRegex}
                      setInvalidInputFound={setInvalidInputFound}
                      setInvalidMandatoryFound={setInvalidMandatoryFound}
                      savedState={savedState}
                      loadSavedState={loadSavedState}
                      setLoadSavedState={setLoadSavedState}
                      setTestsSelected={setTestsSelected}
                      saveTestsForNext={saveTestsForNext}
                      testsSelected={testsSelected}
                      generateSampleID={generateSampleID}
                      setInputValueAutofilled={setInputValueAutofilled}
                      compositeSample={compositeSample}
                      imageInfoRef={imageInfoRef}
                      setImageInfoRef={setImageInfoRef}
                      setLoadingImage={setLoadingImage}
                      submissionEditingSample={submissionEditingSample}
                      submissionAddingSamples={submissionAddingSamples}
                      sampleIdFields={sampleIdFields}
                      disableAddSample={disableAddSample}
                      testInfoMap={testInfoMap}
                      sampleFromSubmission={sampleFromSubmission}
                      submissionDetails={submissionDetails}
                      sampleCategory={sampleCategory}
                      ref={backRef}
                      sampleDetailsColumnRef={sampleDetailsColumnRef}
                      setShowExitModal={setShowExitModal}
                      setIsLoadingTest={setIsLoadingTest}
                      setIsLoadingSampleId={setIsLoadingSampleId}
                      clearTestSearch={clearTestSearch}
                      editingSubmittedSampleComposite={editingSubmittedSampleComposite}
                      setEditingSubmittedSampleComposite={setEditingSubmittedSampleComposite}
                      analysisRequestRef={analysisRequestRef}
                      clearCompositeSaveForNext={clearCompositeSaveForNext}
                    />
                    <AnimatePresence>
                      <motion.div
                        key="SampleDetailPage-MotionDiv"
                        className={`new-submission-pages-col ${!submissionEditingSample ? "analysis-request-column" : "analysis-request-column-edit"}`}
                      >
                        {sampleIDLoaded && (
                          <AnalysisRequest
                            clearTestSearch={clearTestSearch}
                            setClearTestSearch={setClearTestSearch}
                            testsSelected={testsSelected}
                            setTestsSelected={setTestsSelected}
                            customerDetails={customerDetails}
                            submissionEditingSample={submissionEditingSample}
                            sampleID={generatedSampleID}
                            disableAddSample={disableAddSample}
                            setInvalidTest={setInvalidTest}
                            testInfoMap={testInfoMap}
                            pesticideSample={pesticideSample}
                            setPesticideSample={setPesticideSample}
                            inputValuesRefs={inputValuesRefs}
                            setInputValuesRefs={setInputValuesRefs}
                            setInvalidInputFound={setInvalidInputFound}
                            sampleCategory={sampleCategory}
                            AddUpdateSampleButton={addSampleButton}
                            setShowExitModal={setShowExitModal}
                            setEditingSubmittedSampleComposite={setEditingSubmittedSampleComposite}
                            onSaveDraftForRequestNew={onSaveDraftForRequestNew}
                            ref={analysisRequestRef}
                          />
                        )}
                      </motion.div>
                    </AnimatePresence>
                  </div>
                )}
                {minimizedSamplesAfter.length > 0 && (
                  <div className="SampleDetailsPage__MinimizedList SampleDetailsPage__MinimizedList--afterExpanded">
                    {renderMinimizedSampleCards(minimizedSamplesAfter)}
                  </div>
                )}
              </div>
            </StyledScrollbar>
          </div>
          <div className="new-submission-pages-col samplesubmission-exit-button">
            {/* {!isLoggedOut && (
                <ExitButton
                  handleGoBackToMainPage={handleGoBackToMainPage}
                />
              )} */}
          </div>
        </div>
      </div>
      {sampleListSidebar}
    </>
  );
});

export default SampleDetailsPage;
